import React, { useEffect, useState } from "react";
import { Form, Button, Modal, Row, Col, Badge, Card } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  createMeeting,
  fetchMeetings,
  updateMeetingData,
} from "../../../../store/features/meeting/meetingSlice";
import { toast } from "react-toastify";
import {
  fetchAssocitateDataProjectById,
  fetchProjects,
} from "../../../../store/features/project/projectSlice";
import { fetchUserById, fetchUsers } from "../../../../store/features/user/userSlice";
import { formatDateForInput } from "../../../../utils/formatDateForInput";
import { fetchWorkPackages } from "../../../../store/features/workPackage/workPackageSlice";

const MeetingModal = ({
  showForm,
  setShowForm,
  editingMeeting,
  setEditingMeeting,
}) => {
  const { projects, projectsAssociateData } = useSelector(
    (state) => state.projects
  );
  const { workPackages } = useSelector(
      (state) => state.workPackages
    );
  const { usersCreatedBy } = useSelector((state) => state.users);
  const { role, _id } = useSelector((state) => state.auth);
  const [projectId, setProjectId] = useState(editingMeeting?.projectId || "");
  const [otherMeetingType, setOtherMeetingType] = useState("");

  const [attendeesIds, setAttendeesIds] = useState([]);
  const [wpIds, setWpIds] = useState([]);
  const [taskIds, setTaskIds] = useState([]);

  const dispatch = useDispatch();

  const initialValues = React.useMemo(() => {
    return editingMeeting
      ? {
          ...editingMeeting,
          startDate: editingMeeting.startDate
            ? formatDateForInput(editingMeeting.startDate)
            : "",
          endDate: editingMeeting.endDate
            ? formatDateForInput(editingMeeting.endDate)
            : "",
        }
      : {
          title: "",
          organizer: "",
          projectId: "",
          objective: "",
          meetingLink: "",
          meetingPlatform: "",
          startDate: "",
          endDate: "",
          time: "",
          duration: "",
          meetingType: "",
        };
  }, [editingMeeting]);

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Meeting title is required"),
    organizer: Yup.string().required("Organizer is required"),
    meetingLink: Yup.string().required("Meeting link is required"),
    meetingType: Yup.string().required("Meeting type is required"),
    meetingPlatform: Yup.string().required("Meeting platform is required"),
    projectId: Yup.string().required(`${role === "manager" ? "Workpackage" : "Project"} is required`),
    startDate: Yup.date().required("Start date is required"),
    endDate: Yup.date()
      .required("End date is required")
      .min(Yup.ref("startDate"), "End date cannot be before start date"),
    time: Yup.string().required("Time is required"),
    duration: Yup.string().required("Duration is required"),
    objective: Yup.string().required("Objective is required"),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    values.taskIds = taskIds.map((task) => task._id);
    values.wpIds = wpIds.map((wp) => wp._id);
    values.attendeesIds = attendeesIds.map((user) => user._id);
    values.role = role;
    if (values.meetingType === "Others") {
      values.meetingType = otherMeetingType.trim();
    }

    try {
      if (editingMeeting.title) {
        await dispatch(
          updateMeetingData({ ...values, id: editingMeeting._id })
        ).unwrap();
        dispatch(fetchMeetings());
        toast.success("Meeting updated!");
      } else {
        await dispatch(createMeeting(values)).unwrap();
        dispatch(fetchMeetings());
        toast.success("Meeting created!");
      }
      resetForm();
      setShowForm(false);
      setEditingMeeting(null);
    } catch (err) {
      toast.error("Error saving Meeting");
    }
  };

  useEffect(() => {
    if(role==="manager"){
        dispatch(fetchWorkPackages());
    } else{
      dispatch(fetchProjects());
    }
    dispatch(fetchUserById(_id));
  }, [dispatch]);

  useEffect(() => {
    if (editingMeeting.title) {
      setAttendeesIds(editingMeeting.attendeesIds);
      setWpIds(editingMeeting.wpIds);
      setTaskIds(editingMeeting.taskIds);
    } else {
      setAttendeesIds([]);
      setWpIds([]);
      setTaskIds([]);
    }
  }, [editingMeeting]);

  useEffect(() => {
    if (projectsAssociateData && projectId) {
      setAttendeesIds(projectsAssociateData.users || []);
      setWpIds(projectsAssociateData.workPackages || []);
      setTaskIds(projectsAssociateData.tasks || []);
    }
  }, [projectsAssociateData]);

  useEffect(() => {
    if (editingMeeting?.projectId) {
      dispatch(fetchAssocitateDataProjectById(editingMeeting?.projectId));
    }
  }, [editingMeeting?.projectId]);

  const meetingPlatformOptions = [
    "Google Meet",
    "Zoom",
    "Microsoft Teams",
    "Others",
  ];

  const meetingTypesOptions = [
    "Kickoff Meeting",
    "Follow-up Meeting",
    "Review Meeting",
    "Status Update",
    "Planing Session",
    "Retrospective",
    "Traning Session",
    "Transnational Project Meeting",
    "Closing Meeting",
    "Multiplier Event Meeting",
    "Crisis meeting",
    "Steering Committee Meeting",
    "NA Meeting",
    "Audit Meeting",
    "Others",
  ];

  return (
    <Modal size="lg" show={showForm} onHide={() => setShowForm(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {editingMeeting.title ? "Edit Meeting" : "Add Meeting"}
        </Modal.Title>
      </Modal.Header>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          handleChange,
          handleSubmit,
          touched,
          errors,
          isSubmitting,
          setTouched,
          validateForm,
        }) => (
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              validateForm().then((formErrors) => {
                if (Object.keys(formErrors).length > 0) {
                  setTouched(
                    Object.keys(formErrors).reduce((acc, key) => {
                      acc[key] = true;
                      return acc;
                    }, {})
                  );
                  return;
                }
                handleSubmit();
              });
            }}
          >
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Title <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="title"
                      value={values.title}
                      onChange={handleChange}
                      isInvalid={touched.title && errors.title}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.title}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Meeting Type <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="meetingType"
                      value={values.meetingType}
                      onChange={handleChange}
                      isInvalid={touched.meetingType && errors.meetingType}
                    >
                      <option value="">Select Meeting Type</option>
                      {meetingTypesOptions.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                {values.meetingType === "Others" && (
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Specify Other Meeting Type{" "}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={otherMeetingType}
                        onChange={(e) => setOtherMeetingType(e.target.value)}
                        placeholder="Enter custom meeting type"
                        required
                      />
                    </Form.Group>
                  </Col>
                )}

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Meeting Platform <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="meetingPlatform"
                      value={values.meetingPlatform}
                      onChange={handleChange}
                      isInvalid={
                        touched.meetingPlatform && errors.meetingPlatform
                      }
                    >
                      <option value="">Select Platform</option>
                      {meetingPlatformOptions.map((platform) => (
                        <option key={platform} value={platform}>
                          {platform}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.meetingPlatform}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                {role !== "manager" && (
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Project <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="projectId"
                        value={values.projectId}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          handleChange(e);
                          setProjectId(selectedId);
                          dispatch(fetchAssocitateDataProjectById(selectedId));
                        }}
                        isInvalid={touched.projectId && errors.projectId}
                      >
                        <option value="">Select Project</option>
                        {projects.map((project) => (
                          <option key={project._id} value={project._id}>
                            {project.projectName}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.projectId}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                )}

                 {role === "manager" && (
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Workpackage <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="projectId"
                        value={values.projectId}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          handleChange(e);
                          setProjectId(selectedId);
                          dispatch(fetchAssocitateDataProjectById(selectedId));
                        }}
                        isInvalid={touched.projectId && errors.projectId}
                      >
                        <option value="">Select Workpackage</option>
                        {workPackages.map((wp) => (
                          <option key={wp._id} value={wp.projectId}>
                            {wp.title}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.projectId}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                )}
                
                {(wpIds.length > 0 && role !== "manager") && (
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Work Packages ({wpIds.length})</Form.Label>
                      <Card>
                        <Card.Body>
                          {wpIds.map((wp) => (
                            <Badge key={wp._id} bg="secondary" className="me-1">
                              {wp.title}
                            </Badge>
                          ))}
                        </Card.Body>
                      </Card>
                    </Form.Group>
                  </Col>
                )}

                {taskIds.length > 0 && (
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Tasks ({taskIds.length})</Form.Label>
                      <Card>
                        <Card.Body>
                          {taskIds.map((task) => (
                            <Badge
                              key={task._id}
                              bg="secondary"
                              className="me-1"
                            >
                              {task.title}
                            </Badge>
                          ))}
                        </Card.Body>
                      </Card>
                    </Form.Group>
                  </Col>
                )}

                {attendeesIds.length > 0 && (
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Attendees ({attendeesIds.length}){" "}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Card>
                        <Card.Body>
                          {attendeesIds.map((user) => (
                            <Badge
                              key={user._id}
                              bg="secondary"
                              className="me-1"
                            >
                              {user.name}
                              <Button
                                variant="danger"
                                size="sm"
                                className="ms-2 py-0 px-1"
                                onClick={() =>
                                  setAttendeesIds((prev) =>
                                    prev.filter((a) => a._id !== user._id)
                                  )
                                }
                              >
                                ×
                              </Button>
                            </Badge>
                          ))}
                        </Card.Body>
                      </Card>
                    </Form.Group>
                  </Col>
                )}

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Meeting Link <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="meetingLink"
                      value={values.meetingLink}
                      onChange={handleChange}
                      isInvalid={touched.meetingLink && errors.meetingLink}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.meetingLink}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Start Date <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="startDate"
                      value={values.startDate}
                      onChange={handleChange}
                      isInvalid={touched.startDate && errors.startDate}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.startDate}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="endDate"
                      value={values.endDate}
                      onChange={handleChange}
                      isInvalid={touched.endDate && errors.endDate}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.endDate}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Time <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="time"
                      name="time"
                      value={values.time}
                      onChange={handleChange}
                      isInvalid={touched.time && errors.time}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.time}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Duration <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="duration"
                      value={values.duration}
                      onChange={handleChange}
                      isInvalid={touched.duration && errors.duration}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.duration}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Organizer <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="organizer"
                      value={values.organizer}
                      onChange={handleChange}
                      isInvalid={touched.organizer && errors.organizer}
                    >
                      <option value="">Select Organizer</option>
                      {usersCreatedBy.map((user) => (
                        <option key={user._id} value={user._id}>
                          {user.name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.organizer}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Description <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      as="textarea"
                      name="objective"
                      value={values.objective}
                      onChange={handleChange}
                      isInvalid={touched.objective && errors.objective}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.objective}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {editingMeeting.title ? "Update" : "Create"}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default MeetingModal;
